import { Container, FillGradient, Graphics } from 'pixi.js';
import { COLORS, DESIGN_W } from '../app/theme';
import { viewport } from '../app/viewport';
import { tweenProps, easeOutCubic } from '../app/anim';

/**
 * The "tray" panel the table slides in from an edge — used by the action bar (docked), the raise
 * picker (slides up) and the round-result panel (slides down). A wood+gold dark-glass frame: a
 * translucent dark body with a top sheen, a carved double gold border, and a slim gold accent strip
 * on the edge that faces the screen centre. Matches the app's `Panel` chrome (replaces the old
 * silver body + teal strip, which clashed with the gold theme).
 */

export const TRAY_FILL = COLORS.panel; // warm dark wood body (lets the felt read through)
export const TRAY_EDGE = COLORS.gold; // gold accent strip (was teal)
export const TRAY_LABEL_BG = COLORS.darkPill; // dark pill behind tray labels/buttons
export const TRAY_TITLE = COLORS.gold; // gold title text, legible on the dark frame

export type TraySide = 'bottom' | 'top';

const TRAY_RADIUS = 26;

function darken(color: number, f: number): number {
  const r = Math.round(((color >> 16) & 0xff) * f);
  const g = Math.round(((color >> 8) & 0xff) * f);
  const b = Math.round((color & 0xff) * f);
  return (r << 16) | (g << 8) | b;
}

/** Draws the tray body (call for a docked tray that needs no slide animation). */
export function trayBackground(width: number, height: number, side: TraySide): Graphics {
  const g = new Graphics();
  const r = TRAY_RADIUS;
  // Soft drop shadow so the frame lifts off the busy felt.
  g.roundRect(2, 7, width, height, r).fill({ color: COLORS.scrim, alpha: 0.38 });
  // Dark-glass body with a top-lit sheen fading to a darker base for depth.
  g.roundRect(0, 0, width, height, r).fill({ color: TRAY_FILL, alpha: 0.96 });
  g.roundRect(0, 0, width, height, r).fill(
    new FillGradient({
      type: 'linear',
      start: { x: 0, y: 0 },
      end: { x: 0, y: 1 },
      colorStops: [
        { offset: 0, color: 'rgba(255,255,255,0.08)' },
        { offset: 0.42, color: 'rgba(255,255,255,0)' },
        { offset: 1, color: 'rgba(0,0,0,0.30)' },
      ],
    }),
  );
  // Carved gold frame: a recessed dark outer edge + a brighter gold inner hairline.
  g.roundRect(0, 0, width, height, r).stroke({ width: 3, color: darken(COLORS.gold, 0.42), alpha: 0.95 });
  g.roundRect(1.5, 1.5, width - 3, height - 3, r - 1).stroke({ width: 1.5, color: COLORS.gold, alpha: 0.7 });
  // Slim gold accent bar on the inner edge (the one facing screen centre), with a faint glow.
  const barY = side === 'bottom' ? 9 : height - 13;
  g.roundRect(16, barY - 2, width - 32, 8, 4).fill({ color: TRAY_EDGE, alpha: 0.18 });
  g.roundRect(20, barY, width - 40, 4, 2).fill({ color: TRAY_EDGE, alpha: 0.95 });
  return g;
}

/** A tray that slides in/out from the top or bottom edge. Fill `content`; call show()/hide(). */
export class GameTray extends Container {
  /** Caller adds buttons/labels here (origin = tray top-left). */
  readonly content = new Container();
  private readonly shownY: number;
  private readonly hiddenY: number;

  constructor(width: number, height: number, side: TraySide = 'bottom') {
    super();
    this.x = (DESIGN_W - width) / 2;
    this.shownY = side === 'bottom' ? viewport.h - height : 0;
    this.hiddenY = side === 'bottom' ? viewport.h + 4 : -(height + 4);
    this.y = this.hiddenY;
    this.addChild(trayBackground(width, height, side), this.content);
  }

  show(): this {
    tweenProps(this.position as unknown as Record<string, number>, { y: this.shownY }, { duration: 300, ease: easeOutCubic, bind: this });
    return this;
  }

  /** Slide out, then destroy (or run onDone). Safe if already destroyed. */
  hide(onDone?: () => void): void {
    if (this.destroyed) return;
    tweenProps(
      this.position as unknown as Record<string, number>,
      { y: this.hiddenY },
      {
        duration: 220,
        ease: easeOutCubic,
        bind: this,
        onComplete: () => {
          if (onDone) onDone();
          else if (!this.destroyed) this.destroy({ children: true });
        },
      },
    );
  }
}
